export const allocateMonthlyMarkdownActualsToWeeks = ({
  epochWeeks,
  weekToMonthDayCounts,
  monthlyActualMarkdownByEm,
  monthlyActualSalesByEm,
  weeklySalesActualByWeek,
}) => {
  const outByWeek = {}
  const monthDayTotals = new Map()
  const monthSalesTotals = new Map()

  for (const [ew, mMap] of (weekToMonthDayCounts || new Map()).entries()) {
    const totalDaysInWeek = Array.from(mMap.values()).reduce((acc, value) => acc + Number(value || 0), 0)
    const weeklySales = Number(weeklySalesActualByWeek?.[String(ew)] ?? weeklySalesActualByWeek?.[Number(ew)] ?? 0)

    for (const [em, days] of mMap.entries()) {
      const numericEm = Number(em)
      const numericDays = Number(days || 0)
      monthDayTotals.set(numericEm, (monthDayTotals.get(numericEm) || 0) + numericDays)
      const salesContribution = totalDaysInWeek ? (weeklySales * (numericDays / totalDaysInWeek)) : 0
      monthSalesTotals.set(numericEm, (monthSalesTotals.get(numericEm) || 0) + salesContribution)
    }
  }

  ;(epochWeeks || []).forEach((ew) => {
    const mMap = weekToMonthDayCounts?.get?.(Number(ew)) || weekToMonthDayCounts?.get?.(String(ew))
    if (!mMap) {
      outByWeek[String(ew)] = 0
      return
    }

    const totalDaysInWeek = Array.from(mMap.values()).reduce((acc, value) => acc + Number(value || 0), 0)
    const weeklySales = Number(weeklySalesActualByWeek?.[String(ew)] ?? weeklySalesActualByWeek?.[Number(ew)] ?? 0)

    let weekMarkdownActual = 0
    for (const [em, days] of mMap.entries()) {
      const numericEm = Number(em)
      const monthMarkdownActual = Number(monthlyActualMarkdownByEm?.get?.(numericEm) ?? 0)
      if (!monthMarkdownActual) continue

      const numericDays = Number(days || 0)
      const salesContribution = totalDaysInWeek ? (weeklySales * (numericDays / totalDaysInWeek)) : 0
      const monthSalesTotal = Number(monthSalesTotals.get(numericEm) || 0)
      const monthDayTotal = Number(monthDayTotals.get(numericEm) || 0)
      const monthActualSales = Number(monthlyActualSalesByEm?.get?.(numericEm) ?? 0)

      let weight = 0
      if (monthActualSales > 0 && monthSalesTotal > 0) {
        weight = salesContribution / monthSalesTotal
      } else if (monthDayTotal > 0) {
        weight = numericDays / monthDayTotal
      }

      weekMarkdownActual += monthMarkdownActual * weight
    }

    outByWeek[String(ew)] = weekMarkdownActual
  })

  return outByWeek
}

// Returns the dominant epoch month for a week's mMap (most days; tie-break: earlier month).
const getDominantEmForMap = (mMap) => {
  let dominantEm = null
  let maxDays = -1
  for (const [em, days] of mMap.entries()) {
    const d = Number(days || 0)
    const n = Number(em)
    if (d > maxDays || (d === maxDays && dominantEm !== null && n < dominantEm)) {
      maxDays = d
      dominantEm = n
    }
  }
  return dominantEm
}

export const allocateMonthlyInflowActualsToWeeks = ({
  epochWeeks,
  weekToMonthDayCounts,
  monthlyActualInflowByEm,
}) => {
  const outByWeek = {}

  // Build monthDayTotals counting ONLY days from weeks where that month is dominant.
  // This mirrors how the weekly grid groups weeks into month subtotals via representativeMonthKey,
  // so that summing the weekly inflow actuals by month equals the monthly actual totals.
  const monthDayTotals = new Map()
  for (const [, mMap] of (weekToMonthDayCounts || new Map()).entries()) {
    const domEm = getDominantEmForMap(mMap)
    if (domEm === null) continue
    const domDays = Number(mMap.get(domEm) || 0)
    monthDayTotals.set(domEm, (monthDayTotals.get(domEm) || 0) + domDays)
  }

  ;(epochWeeks || []).forEach((ew) => {
    const mMap = weekToMonthDayCounts?.get?.(Number(ew)) || weekToMonthDayCounts?.get?.(String(ew))
    if (!mMap) {
      outByWeek[String(ew)] = 0
      return
    }

    // Only attribute the dominant month's inflow to this week, matching how the
    // weekly grid groups weeks into month subtotals (representativeMonthKey).
    const domEm = getDominantEmForMap(mMap)
    if (domEm === null) {
      outByWeek[String(ew)] = 0
      return
    }

    const monthInflowActual = Number(monthlyActualInflowByEm?.get?.(domEm) ?? 0)
    const monthDayTotal = Number(monthDayTotals.get(domEm) || 0)
    const domDays = Number(mMap.get(domEm) || 0)
    if (!monthDayTotal) {
      outByWeek[String(ew)] = 0
      return
    }

    outByWeek[String(ew)] = monthInflowActual * (domDays / monthDayTotal)
  })

  return outByWeek
}

export const allocateMonthlySalesActualsToWeeks = ({
  epochWeeks,
  weekToMonthDayCounts,
  monthlyActualSalesByEm,
}) => {
  const outByWeek = {}
  const monthDayTotals = new Map()

  for (const [, mMap] of (weekToMonthDayCounts || new Map()).entries()) {
    for (const [em, days] of mMap.entries()) {
      const numericEm = Number(em)
      monthDayTotals.set(numericEm, (monthDayTotals.get(numericEm) || 0) + Number(days || 0))
    }
  }

  ;(epochWeeks || []).forEach((ew) => {
    const mMap = weekToMonthDayCounts?.get?.(Number(ew)) || weekToMonthDayCounts?.get?.(String(ew))
    if (!mMap) {
      outByWeek[String(ew)] = 0
      return
    }

    let weekSalesActual = 0
    for (const [em, days] of mMap.entries()) {
      const numericEm = Number(em)
      const monthSalesActual = Number(monthlyActualSalesByEm?.get?.(numericEm) ?? 0)
      const monthDayTotal = Number(monthDayTotals.get(numericEm) || 0)
      if (!monthSalesActual || !monthDayTotal) continue
      weekSalesActual += monthSalesActual * (Number(days || 0) / monthDayTotal)
    }

    outByWeek[String(ew)] = weekSalesActual
  })

  return outByWeek
}

